/**
 * Generalized photo-first document verification state machine.
 *
 * Factored out of use-identity-verification.ts so the license flow (and
 * any future document-type flow like cedula, NIE) can reuse the same
 * state progression, capture handlers, haptics, and error recovery
 * without copy-paste.
 *
 * State progression:
 *   idle → scanning-front → scanning-back? → uploading → verifying →
 *     extracted → confirmed (happy path)
 *     declined | error (recovery path)
 *
 * The caller provides a `submit()` function that runs the actual upload +
 * edge-function call. Everything else (scanner launch, web fallback,
 * haptics, phase transitions) is handled in here.
 */
import { useCallback, useState } from 'react'
import { Platform } from 'react-native'
import { impact, ImpactStyle, notification, NotificationType } from '../utils/haptics'
import * as ImagePicker from 'expo-image-picker'
import {
  isCameraPermissionError,
  scanDocumentOutcome,
  type ScanOutcome,
} from '@/components/hosting/DocumentScanner'
import type {
  CanonicalVerificationStatus,
  ExtractedIdentityData,
  VerificationProviderName,
} from '@/lib/types/verification.types'
import { ServiceError } from '@/lib/errors/service-error'
import i18n from '@/lib/i18n/config'
import { captureError, errorMessage } from '@/lib/utils/observability'

export type VerificationPhase =
  | 'idle'
  | 'scanning-front'
  | 'scanning-back'
  | 'uploading'
  | 'verifying'
  | 'extracted'
  | 'confirmed'
  | 'declined'
  | 'resume'
  | 'error'

export interface DocumentVerificationState {
  nextStep?: 'selfie'
  phase: VerificationPhase
  recordId: string | null
  frontUri: string | null
  backUri: string | null
  extracted: ExtractedIdentityData
  provider: VerificationProviderName | null
  canonicalStatus: CanonicalVerificationStatus | null
  confidence: number | null
  error: string | null
  /**
   * El fallo de captura fue un permiso de cámara bloqueado: reintentar no
   * cambia nada y la pantalla tiene que ofrecer Ajustes. Sin esto, el reintento
   * es un bucle contra una decisión del sistema operativo.
   */
  errorNeedsSettings: boolean
  /**
   * True when the last submit lost the provider response (timeout/network)
   * but the evidence IS persisted — the next action is resume (reuse the
   * stored photos), never a blind recapture.
   */
  canResume: boolean
}

export interface DocumentVerificationSubmitResult {
  nextStep?: 'selfie'
  recordId?: string
  status: CanonicalVerificationStatus
  provider: VerificationProviderName
  extracted: ExtractedIdentityData
  confidence?: number
  rejectionReason?: string | null
}

export interface DocumentVerificationConfig {
  /** If false, only front photo is captured and the flow skips straight to upload. */
  requireBack: boolean
  /** If false, capture only stores images; the caller decides when to submit. */
  autoSubmit?: boolean
  /** Submit function. Throw ServiceError on failure. */
  submit: (input: {
    frontImageUri: string
    backImageUri: string | null
  }) => Promise<DocumentVerificationSubmitResult>
  /**
   * Re-run verification against an ALREADY-persisted record (same evidence,
   * same row). Explicit user action after a lost response — never automatic.
   */
  resumeSubmit?: (recordId: string) => Promise<DocumentVerificationSubmitResult>
}

const initialState: DocumentVerificationState = {
  phase: 'idle',
  recordId: null,
  frontUri: null,
  backUri: null,
  extracted: {},
  provider: null,
  canonicalStatus: null,
  confidence: null,
  error: null,
  errorNeedsSettings: false,
  canResume: false,
}

export function useDocumentVerificationStep(config: DocumentVerificationConfig) {
  const [state, setState] = useState<DocumentVerificationState>(initialState)
  const autoSubmit = config.autoSubmit !== false

  const runSubmission = useCallback(
    async (frontUri: string, backUri: string | null) => {
      setState((s) => ({ ...s, phase: 'verifying' }))
      impact(ImpactStyle.Light)
      try {
        const result = await config.submit({
          frontImageUri: frontUri,
          backImageUri: backUri,
        })

        const hasReadableData = hasReadableExtraction(result.extracted)
        const terminal: VerificationPhase =
          result.status === 'approved'
            ? 'confirmed'
            : result.status === 'declined'
              ? 'declined'
              : hasReadableData
                ? 'extracted'
                : 'error'

        if (result.status === 'approved') {
          notification(NotificationType.Success)
        } else if (result.status === 'declined') {
          notification(NotificationType.Warning)
        }

        setState((s) => ({
          ...s,
          phase: terminal,
          recordId: result.recordId ?? s.recordId,
          extracted: result.extracted,
          provider: result.provider,
          canonicalStatus: result.status,
          confidence: result.confidence ?? null,
          error:
            terminal === 'error'
              ? (result.rejectionReason ?? i18n.t('verification.error_scan_empty'))
              : (result.rejectionReason ?? null),
        }))
      } catch (err) {
        notification(NotificationType.Error)
        const message =
          err instanceof ServiceError
            ? errorMessage(err, i18n.t('verification.error_failed'))
            : i18n.t('verification.error_failed')
        // The response was lost, not refused. The evidence IS saved: the honest
        // next action is resume (reuse the stored photos), never "start over".
        const lostResponse = err instanceof ServiceError && err.code === 'VERIFICATION_INCOMPLETE'
        const persistedRecordId = persistedRecordIdFromError(err)
        setState((s) => {
          const recordId = persistedRecordId ?? s.recordId
          return {
            ...s,
            phase: 'error',
            recordId,
            error: message,
            canResume: lostResponse && Boolean(recordId),
          }
        })
      }
    },
    [config],
  )

  const captureFront = useCallback(async () => {
    setState((s) => ({ ...s, phase: 'scanning-front', error: null, errorNeedsSettings: false }))
    const outcome = await openCapture()
    if (outcome.status === 'error') {
      setState((s) => ({ ...s, phase: 'error', ...captureFailure(outcome.error, 'front') }))
      return
    }
    if (outcome.status === 'cancel') {
      setState((s) => ({ ...s, phase: 'idle' }))
      return
    }
    const uri = outcome.uri
    notification(NotificationType.Success)
    setState((s) => ({
      ...s,
      frontUri: uri,
      phase: config.requireBack ? 'scanning-back' : autoSubmit ? 'uploading' : 'extracted',
    }))
    if (!config.requireBack && autoSubmit) {
      void runSubmission(uri, null)
    }
  }, [autoSubmit, config.requireBack, runSubmission])

  const captureBack = useCallback(async () => {
    const front = state.frontUri
    if (!front) {
      setState((s) => ({ ...s, phase: 'idle' }))
      return
    }
    const outcome = await openCapture()
    if (outcome.status === 'error') {
      setState((s) => ({ ...s, phase: 'error', ...captureFailure(outcome.error, 'back') }))
      return
    }
    if (outcome.status === 'cancel') {
      setState((s) => ({ ...s, phase: 'scanning-back' }))
      return
    }
    const uri = outcome.uri
    notification(NotificationType.Success)
    setState((s) => ({ ...s, backUri: uri, phase: autoSubmit ? 'uploading' : 'extracted' }))
    if (autoSubmit) {
      void runSubmission(front, uri)
    }
  }, [autoSubmit, state.frontUri, runSubmission])

  const retakeFront = useCallback(async () => {
    setState((s) => ({ ...s, phase: 'scanning-front', error: null, errorNeedsSettings: false }))
    const outcome = await openCapture()
    if (outcome.status === 'error') {
      setState((s) => ({
        ...s,
        phase: 'error',
        ...captureFailure(outcome.error, 'retake-front'),
      }))
      return
    }
    if (outcome.status === 'cancel') {
      setState((s) => ({ ...s, phase: state.backUri ? 'extracted' : 'idle' }))
      return
    }
    const uri = outcome.uri
    notification(NotificationType.Success)
    const canSubmit = autoSubmit && (!config.requireBack || Boolean(state.backUri))
    setState((s) => ({
      ...s,
      frontUri: uri,
      phase: canSubmit ? 'uploading' : state.backUri ? 'extracted' : 'scanning-back',
    }))
    if (canSubmit) {
      void runSubmission(uri, state.backUri)
    }
  }, [autoSubmit, config.requireBack, state.backUri, runSubmission])

  const retakeBack = useCallback(async () => {
    const front = state.frontUri
    if (!front) return
    setState((s) => ({ ...s, phase: 'scanning-back', error: null, errorNeedsSettings: false }))
    const outcome = await openCapture()
    if (outcome.status === 'error') {
      setState((s) => ({
        ...s,
        phase: 'error',
        ...captureFailure(outcome.error, 'retake-back'),
      }))
      return
    }
    if (outcome.status === 'cancel') {
      setState((s) => ({ ...s, phase: 'extracted' }))
      return
    }
    const uri = outcome.uri
    notification(NotificationType.Success)
    setState((s) => ({ ...s, backUri: uri, phase: autoSubmit ? 'uploading' : 'extracted' }))
    if (autoSubmit) {
      void runSubmission(front, uri)
    }
  }, [autoSubmit, state.frontUri, runSubmission])

  const updateField = useCallback((key: keyof ExtractedIdentityData, value: string) => {
    setState((s) => ({
      ...s,
      extracted: { ...s.extracted, [key]: value.trim() || null },
    }))
  }, [])

  const applyResult = useCallback((result: DocumentVerificationSubmitResult) => {
    setState((s) => ({
      ...s,
      ...nextResultState(s, result),
      canResume: false,
    }))
  }, [])

  const markConfirmed = useCallback(() => {
    setState((s) => ({ ...s, phase: 'confirmed', error: null }))
  }, [])

  /**
   * Re-run verification against the persisted record after a lost response.
   * Requires `recordId` (set by the submit that stored the evidence) and the
   * `resumeSubmit` continuation.
   */
  const resume = useCallback(async () => {
    if (!state.recordId || !config.resumeSubmit) return
    setState((s) => ({ ...s, phase: 'verifying', error: null, canResume: false }))
    impact(ImpactStyle.Light)
    try {
      const result = await config.resumeSubmit(state.recordId)
      applyResult(result)
    } catch (err) {
      notification(NotificationType.Error)
      const message =
        err instanceof ServiceError
          ? errorMessage(err, i18n.t('verification.error_failed'))
          : i18n.t('verification.error_failed')
      const lostResponse = err instanceof ServiceError && err.code === 'VERIFICATION_INCOMPLETE'
      const persistedRecordId = persistedRecordIdFromError(err)
      setState((s) => {
        const recordId = persistedRecordId ?? s.recordId
        return {
          ...s,
          phase: 'error',
          recordId,
          error: message,
          canResume: lostResponse && Boolean(recordId),
        }
      })
    }
  }, [applyResult, config, state.recordId])

  /**
   * Hydrate the flow from a persisted submission on mount: the record id and
   * the already-extracted fields, with NO local URIs (the evidence lives in
   * storage). The person reviews and confirms — they never recapture.
   *
   * `phase` defaults to 'extracted' (fields shown, confirm available). Pass
   * 'resume' when the row has stored photos but nothing was read yet.
   */
  const hydrate = useCallback(
    (
      submission: {
        recordId: string
        extracted: ExtractedIdentityData
        canonicalStatus: CanonicalVerificationStatus | null
      },
      phase: 'extracted' | 'resume' = 'extracted',
    ) => {
      setState((s) => ({
        ...s,
        phase,
        recordId: submission.recordId,
        extracted: submission.extracted,
        canonicalStatus: submission.canonicalStatus,
        provider: null,
        confidence: null,
        error: null,
        canResume: false,
      }))
    },
    [],
  )

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  return {
    state,
    captureFront,
    captureBack,
    retakeFront,
    retakeBack,
    updateField,
    applyResult,
    markConfirmed,
    resume,
    hydrate,
    reset,
  }
}

function persistedRecordIdFromError(error: unknown): string | null {
  if (!(error instanceof ServiceError)) return null
  const value = error.details?.persistedRecordId
  return typeof value === 'string' && value.length > 0 ? value : null
}

function hasReadableExtraction(extracted: ExtractedIdentityData | undefined): boolean {
  if (!extracted) return false
  return Boolean(
    extracted.firstName ||
    extracted.lastName ||
    extracted.fullName ||
    extracted.dateOfBirth ||
    extracted.documentNumber ||
    extracted.duiNumber ||
    extracted.controlNumber ||
    extracted.expiryDate,
  )
}

function nextResultState(
  current: DocumentVerificationState,
  result: DocumentVerificationSubmitResult,
): Partial<DocumentVerificationState> {
  const extracted = { ...current.extracted, ...result.extracted }
  const declinedButEditable = result.status === 'declined' && hasReadableExtraction(extracted)
  const manualReviewSubmitted = result.status === 'review' || result.status === 'in_progress'
  const terminal: VerificationPhase =
    result.status === 'approved'
      ? 'confirmed'
      : manualReviewSubmitted
        ? 'confirmed'
        : declinedButEditable
          ? 'extracted'
          : result.status === 'declined'
            ? 'declined'
            : 'error'

  return {
    phase: terminal,
    nextStep: result.nextStep,
    recordId: result.recordId ?? current.recordId,
    extracted,
    provider: result.provider,
    canonicalStatus: result.status,
    confidence: result.confidence ?? current.confidence,
    error:
      result.rejectionReason ??
      (terminal === 'error' ? i18n.t('verification.verifik_retry_required') : null),
  }
}

/**
 * Cross-platform photo capture. Uses the native DocumentScanner plugin (VisionKit / ML Kit) on
 * iOS + Android for real edge detection + auto-crop + perspective correction. On web, camera
 * failures are reported instead of silently falling back to stored files.
 *
 * RETURNS AN OUTCOME, NOT A URI-OR-NULL.
 *
 * It used to return `string | null` via the `scanDocument()` back-compat wrapper, which
 * collapses "the person tapped back" and "the scanner could not start" into the same `null`.
 * Every caller here read that as a cancel. So on an Android device where ML Kit's scanner module
 * cannot install — a real, common failure on devices without an up-to-date Play Services — the
 * capture button returned the screen to `idle` and did nothing. No error, no retry, no row, and
 * no Sentry event, which is also why there is no data on how often it happens. For a DUI, which
 * is the default, the person was then left staring at a permanently disabled "Verificar mi DUI"
 * under a checklist reading "Falta la foto del frente".
 *
 * A cancel and a failure need different answers, so they must stay different values all the way
 * to the screen.
 */
async function openCapture(): Promise<ScanOutcome> {
  if (Platform.OS === 'web') {
    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        quality: 0.85,
        allowsEditing: false,
      })
      if (result.canceled || !result.assets[0]) return { status: 'cancel' }
      return { status: 'ok', uri: result.assets[0].uri }
    } catch (error) {
      // Same distinction on web: a denied permission or an absent camera is not a cancel.
      return { status: 'error', error }
    }
  }
  // La cadena escáner → cámara del sistema vive en DocumentScanner
  // (`scanDocumentOutcome`), que es su único dueño. Duplicarla acá fue el error
  // que este archivo ya no comete.
  return scanDocumentOutcome()
}

/**
 * What the screen does when the scanner itself fails.
 *
 * Reported, always — this failure has been invisible for the app's whole life, so the first
 * thing it needs is to exist in telemetry. Then told to the person, because a button that does
 * nothing is indistinguishable from a broken app.
 *
 * Un permiso de cámara bloqueado se dice distinto: no es "el escáner no está
 * disponible", es una decisión del sistema que solo se revierte en Ajustes, y
 * la pantalla necesita saberlo para ofrecer ese camino en vez de un reintento
 * que va a fallar igual.
 */
function captureFailure(
  error: unknown,
  step: string,
): Pick<DocumentVerificationState, 'error' | 'errorNeedsSettings'> {
  captureError(error, { screen: 'verification_document', action: 'open-capture' }, { step })
  if (isCameraPermissionError(error)) {
    return {
      error: i18n.t('verification.camera_permission_blocked'),
      errorNeedsSettings: true,
    }
  }
  return { error: i18n.t('verification.scanner_unavailable'), errorNeedsSettings: false }
}
